import { Paper, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import classNames from 'classnames'

import { getLabelByIncomeDecile } from 'components/SegmentSelector/DemographicsSelector/NeighborhoodIncomeSelector'
import { getUserTypeDisplay } from 'components/SegmentSelector/UserTypeSelector'
import {
  AUTOMATED_TRIGGER_CONFIGS,
  ONE_TIME_TRIGGER,
} from 'components/SegmentSelector/outreach-message-trigger-constants'
import {
  getIsUsingManualStudentSelection,
  getIsUsingMyMatches,
  getSegmentOperation,
  getSegmentValue,
} from 'components/SegmentSelector/segment-service'

import { Message } from '../types'

const DEFAULT_GRAD_YEARS = [0, 1, 2, 3]
const DEFAULT_ENGAGEMENTS = 'Following, Applying, Applied'
const CLASS_YEAR_CONFIG = {
  3: 'Freshmen',
  2: 'Sophomores',
  1: 'Juniors',
  0: 'Seniors',
}
const getLegalSexLabel = (value: 'male' | 'female' | 'everyone') => {
  switch (value) {
    case 'male':
      return 'Male'
    case 'female':
      return 'Female'
    default:
      return 'Everyone'
  }
}
const getCommunityTypeLabel = (value: 'city' | 'suburban' | 'town' | 'rural') => {
  switch (value) {
    case 'city':
      return 'City'
    case 'suburban':
      return 'Suburban'
    case 'town':
      return 'Town'
    case 'rural':
      return 'Rural'
    default:
      return ''
  }
}
const getNeighborhoodIncomeLabel = (value: [number, number]) => {
  const from = value[0] - 1 // subtract 1 to the from value to get the correct label
  const to = value[1]
  const fromLabel = getLabelByIncomeDecile(from)
  const toLabel = getLabelByIncomeDecile(to)
  return `Min Median: ${fromLabel} to Max Median: ${toLabel}`
}

const arrayToString = (arr: string[], separator = ', ') => arr.join(separator)

const getSegmentInfo = (segment: Message['Segment'], type: string, segmentName: string) => {
  const values = getSegmentValue(segment, type, segmentName)
  const operation = getSegmentOperation(segment, type, segmentName)
  const hasValues = values.length > 0
  return [hasValues, values, operation]
}
const getEngagementInfo = (segment: Message['Segment']) => {
  let engagement = DEFAULT_ENGAGEMENTS
  if (segment) {
    engagement = arrayToString(getSegmentInfo(segment, 'Must', 'EngagementStage')[1])
  }
  return engagement
}

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  value: {
    width: '252px',
  },
  testSection: {
    marginTop: '4px',
  },
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(3),
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
}))

const AudienceDetails = ({ message }: { message: Message }) => {
  const classes = useStyles()
  const segment = message.Segment
  const [hasInterests, interests] = getSegmentInfo(segment, 'OneOf', 'Interests')
  const [hasAcademicPrefs, academicPrefs] = getSegmentInfo(segment, 'OneOf', 'AcademicFocus')
  const [hasCareers, careers] = getSegmentInfo(segment, 'Must', 'Careers')
  const [hasSatScores, satScores] = getSegmentInfo(segment, 'OneOf', 'SAT')
  const [hasSATMathScores, satMathScores] = getSegmentInfo(segment, 'OneOf', 'SATMath')
  const [hasSATReadingWritingScores, satReadingWritingScores] = getSegmentInfo(segment, 'OneOf', 'SATReadingWriting')
  const [hasActScores, actScores] = getSegmentInfo(segment, 'OneOf', 'ACT')
  const [hasACTMathScores, actMathScores] = getSegmentInfo(segment, 'OneOf', 'ACTMath')
  const [hasACTReadingScores, actReadingScores] = getSegmentInfo(segment, 'OneOf', 'ACTReading')
  const [hasACTScienceScores, actScienceScores] = getSegmentInfo(segment, 'OneOf', 'ACTScience')
  const [hasACTEnglishScores, actEnglishScores] = getSegmentInfo(segment, 'OneOf', 'ACTEnglish')
  const [hasPactScores, pactScores] = getSegmentInfo(segment, 'OneOf', 'PACT')
  const [hasPsatNmsqtScores, psatNmsqtScores] = getSegmentInfo(segment, 'OneOf', 'PSATNMSQT')
  const [hasPsatNmsqtMathScores, psatNmsqtMathScores] = getSegmentInfo(segment, 'OneOf', 'NMSQTMath')
  const [hasPsatNmsqtReadingWritingScores, psatNmsqtReadingWritingScores] = getSegmentInfo(
    segment,
    'OneOf',
    'NMSQTReadingWriting',
  )
  const [hasRace, race] = getSegmentInfo(segment, 'Must', 'Race')
  const [hasEthnicity, ethnicity] = getSegmentInfo(segment, 'Must', 'Ethnicity')
  const [hasPsat10Scores, psat10Scores] = getSegmentInfo(segment, 'OneOf', 'PSAT10')
  const [hasPsat89Scores, psat89Scores] = getSegmentInfo(segment, 'OneOf', 'PSAT89')
  const [hasNeighborhoodIncome, neighborhoodIncome] = getSegmentInfo(segment, 'Must', 'NeighborhoodIncome')
  const [hasCommunityType, communityType] = getSegmentInfo(segment, 'Must', 'NeighborhoodType')
  const [hasState, states] = getSegmentInfo(segment, 'Must', 'USAState')
  const [hasHighSchool, highschools] = getSegmentInfo(segment, 'Must', 'HighSchool')
  const [hasIsPrivate, isPrivate] = getSegmentInfo(segment, 'Must', 'IsPrivate')
  const hasReducedLunchValue = getSegmentValue(segment, 'Must', 'HasReducedLunch')
  const legalSex = getSegmentValue(segment, 'Must', 'LegalSex')

  const [hasFirstGen, , firstGenOperation] = getSegmentInfo(segment, 'Must', 'FirstGeneration')
  const firstGen = firstGenOperation === 'in'
  const [hasKnownStudent, knownStudentValues, knownStudentOperation] = getSegmentInfo(segment, 'Must', 'IsKnown')
  const knownStudent = knownStudentOperation === 'in' && knownStudentValues[0] === true
  const unknownStudent = knownStudentOperation === 'in' && knownStudentValues[0] === false
  let gradYears = getSegmentInfo(segment, 'Must', 'CurrentGradYearOffset')[1]
  if (!gradYears) {
    gradYears = DEFAULT_GRAD_YEARS
  }

  gradYears = gradYears
    .slice() // avoid mutating the original array
    .sort((a: number, b: number) => b - a) // sorts in descending order

  const isOneTime = message.Trigger === ONE_TIME_TRIGGER
  const triggers = isOneTime
    ? getEngagementInfo(segment)
    : AUTOMATED_TRIGGER_CONFIGS.find((config) => config.value === message.Trigger)?.label ?? 'Unknown'
  const engagementHeading = isOneTime ? 'Engagement Stage' : 'Trigger'
  const isUsingManualStudentSelection = getIsUsingManualStudentSelection(segment)
  const isUsingMyMatches = getIsUsingMyMatches(segment)
  const audienceDetails = [
    {
      section: getUserTypeDisplay(message.Segment),
      values: isUsingManualStudentSelection
        ? 'Custom List of Student IDs'
        : isUsingMyMatches
          ? 'My Matches'
          : 'Followers',
      display: true,
    },
    {
      section: engagementHeading,
      values: triggers,
      display: true,
    },
    {
      section: 'Class Year',
      values: arrayToString(gradYears.map((year: keyof typeof CLASS_YEAR_CONFIG) => CLASS_YEAR_CONFIG[year])),
      display: true,
    },
    {
      section: 'Academic Focus',
      values: arrayToString(academicPrefs),
      display: hasAcademicPrefs,
    },
    {
      section: 'Student Life',
      values: arrayToString(interests),
      display: hasInterests,
    },
    {
      section: 'Career Interests',
      values: arrayToString(careers, '; '),
      display: hasCareers,
    },
    {
      section: 'Known Students',
      values: unknownStudent
        ? 'Unknown Students Only'
        : knownStudent
          ? 'Known Students Only'
          : 'All Students',
      display: hasKnownStudent,
    },
    {
      section: 'First Generation',
      values: firstGen ? 'First Generation Students Only' : 'Non-First Generation Students Only',
      display: hasFirstGen,
    },
    {
      section: 'Legal Sex',
      values: getLegalSexLabel(arrayToString(legalSex) as 'male' | 'female' | 'everyone'),
      display: true,
    },
    {
      section: 'Race',
      values: arrayToString(race),
      display: hasRace,
    },
    {
      section: 'Hispanic or Latino',
      values: ethnicity ? 'Yes' : 'No',
      display: hasEthnicity,
    },
    {
      section: 'Neighborhood Household Income',
      values: getNeighborhoodIncomeLabel(neighborhoodIncome),
      display: hasNeighborhoodIncome,
    },
    {
      section: 'Student High School State',
      values: arrayToString(states),
      display: hasState,
    },
    {
      section: 'Community Type',
      values: communityType.map(getCommunityTypeLabel).join(', '),
      display: hasCommunityType,
    },
    {
      section: 'Student High School',
      values: arrayToString(highschools.map((hs: { name: string }) => hs.name)),
      display: hasHighSchool,
    },
    {
      section: 'High School Type',
      values: isPrivate[0] ? 'Private' : hasReducedLunchValue[0] ? 'Title I Eligible' : 'Public',
      display: hasIsPrivate,
    },
    {
      section: 'SAT Scores',
      values: '',
      display: hasSatScores || hasSATMathScores || hasSATReadingWritingScores,
      isTestGroup: true,
      subjectScores: [
        {
          label: 'Overall',
          value: hasSatScores ? `${satScores[0]} - ${satScores[1]}` : null,
        },
        {
          label: 'Math',
          value: hasSATMathScores ? `${satMathScores[0]} - ${satMathScores[1]}` : null,
        },
        {
          label: 'Reading & Writing',
          value: hasSATReadingWritingScores ? `${satReadingWritingScores[0]} - ${satReadingWritingScores[1]}` : null,
        },
      ],
    },
    {
      section: 'ACT Scores',
      values: '',
      display: hasActScores || hasACTMathScores || hasACTReadingScores || hasACTScienceScores || hasACTEnglishScores,
      isTestGroup: true,
      subjectScores: [
        {
          label: 'Overall',
          value: hasActScores ? `${actScores[0]} - ${actScores[1]}` : null,
        },
        {
          label: 'Math',
          value: hasACTMathScores ? `${actMathScores[0]} - ${actMathScores[1]}` : null,
        },
        {
          label: 'Reading',
          value: hasACTReadingScores ? `${actReadingScores[0]} - ${actReadingScores[1]}` : null,
        },
        {
          label: 'Science',
          value: hasACTScienceScores ? `${actScienceScores[0]} - ${actScienceScores[1]}` : null,
        },
        {
          label: 'English',
          value: hasACTEnglishScores ? `${actEnglishScores[0]} - ${actEnglishScores[1]}` : null,
        },
      ],
    },
    {
      section: 'PSAT/NMSQT Scores',
      values: '',
      display: hasPsatNmsqtScores || hasPsatNmsqtMathScores || hasPsatNmsqtReadingWritingScores,
      isTestGroup: true,
      subjectScores: [
        {
          label: 'Overall',
          value: hasPsatNmsqtScores ? `${psatNmsqtScores[0]} - ${psatNmsqtScores[1]}` : null,
        },
        {
          label: 'Math',
          value: hasPsatNmsqtMathScores ? `${psatNmsqtMathScores[0]} - ${psatNmsqtMathScores[1]}` : null,
        },
        {
          label: 'Reading & Writing',
          value: hasPsatNmsqtReadingWritingScores
            ? `${psatNmsqtReadingWritingScores[0]} - ${psatNmsqtReadingWritingScores[1]}`
            : null,
        },
      ],
    },
    {
      section: 'Pre-ACT Score Range',
      values: `${pactScores[0]} - ${pactScores[1]}`,
      display: hasPactScores,
    },
    {
      section: 'PSAT 10 Score Range',
      values: `${psat10Scores[0]} - ${psat10Scores[1]}`,
      display: hasPsat10Scores,
    },
    {
      section: 'PSAT 8/9 Score Range',
      values: `${psat89Scores[0]} - ${psat89Scores[1]}`,
      display: hasPsat89Scores,
    },
  ]
  return (
    <Paper
      elevation={0}
      className={classes.paper}
    >
      <Typography variant="h6">Audience</Typography>
      <div>
        {audienceDetails.map(
          (segmentType, i) =>
            segmentType.display && (
              <div
                key={i}
                className={classes.container}
              >
                <Typography variant="body1">{segmentType.section}</Typography>
                <div className={classes.details}>
                  {segmentType.isTestGroup && segmentType.subjectScores ? (
                    <div className={classNames(classes.testSection, classes.value)}>
                      {segmentType.subjectScores.map(
                        (subject, idx) =>
                          subject.value && (
                            <Typography
                              key={idx}
                              variant="body2"
                            >
                              {subject.label}: {subject.value}
                            </Typography>
                          ),
                      )}
                    </div>
                  ) : (
                    <Typography
                      variant="body1"
                      className={classes.value}
                    >
                      {segmentType.values}
                    </Typography>
                  )}
                </div>
              </div>
            ),
        )}
      </div>
    </Paper>
  )
}

export default AudienceDetails
